import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { departmentService } from "@/services/DepartmentService";
import { organizationService } from "@/services/OrganizationService";
import { locationService } from "@/services/LocationService";
import { locationTypeLookupService } from "@/services/lookup/LocationTypeLookupService";

/**
 * Hook for prefetching admin-related data
 * Provides intelligent prefetching for related admin queries
 */
export const useAdminPrefetch = () => {
    const queryClient = useQueryClient();

    const prefetchDepartments = useCallback(async () => {
        try {
            await queryClient.prefetchQuery({
                queryKey: ["departments"],
                queryFn: () => departmentService.getDepartments(),
                staleTime: 15 * 60 * 1000, // 15 minutes to align with hooks
            });
        } catch (error) {
            console.warn("Failed to prefetch departments:", error);
        }
    }, [queryClient]);

    const prefetchOrganizations = useCallback(async () => {
        try {
            await queryClient.prefetchQuery({
                queryKey: ["organizations"],
                queryFn: () => organizationService.getOrganizations(),
                staleTime: 15 * 60 * 1000, // 15 minutes to align with global settings
            });
        } catch (error) {
            console.warn("Failed to prefetch organizations:", error);
        }
    }, [queryClient]);

    const prefetchLocations = useCallback(async () => {
        try {
            // Use the EXACT same filter structure as the location page
            const filters = {
                pageNumber: 1,
                pageSize: 10,
                locationTypeId: undefined,
                locationName: undefined,
                stateCode: undefined,
            };
            await queryClient.prefetchQuery({
                queryKey: ["locationList", filters],
                queryFn: () => locationService.getLocationList(filters),
                staleTime: 5 * 60 * 1000, // 5 minutes for paginated data
            });
        } catch (error) {
            console.warn("Failed to prefetch locations:", error);
        }
    }, [queryClient]);

    const prefetchLocationLookups = useCallback(async () => {
        // Prefetch location types and other lookup data commonly used in forms
        await queryClient.prefetchQuery({
            queryKey: ["lookup", "locationTypes"],
            queryFn: () => locationTypeLookupService.getLocationTypes(),
            staleTime: 24 * 60 * 60 * 1000, // 24 hours for lookup data
        });
    }, [queryClient]);

    /**
     * Prefetches all admin data in optimal order
     * Departments -> Organizations -> Locations (with dependencies)
     */
    const prefetchAllAdminData = useCallback(async () => {
        try {
            // Prefetch in parallel for better performance
            await Promise.all([
                prefetchDepartments(),
                prefetchOrganizations(),
                prefetchLocationLookups(),
            ]);

            // Prefetch locations after lookups are available
            await prefetchLocations();
        } catch (error) {
            console.warn("Admin data prefetch failed:", error);
        }
    }, [
        prefetchDepartments,
        prefetchOrganizations,
        prefetchLocations,
        prefetchLocationLookups,
    ]);

    /**
     * Prefetches data related to a specific admin page
     */
    const prefetchAdminPage = useCallback(
        async (page: "department" | "organization" | "location") => {
            try {
                switch (page) {
                    case "department":
                        await Promise.all([
                            prefetchDepartments(),
                            prefetchOrganizations(),
                        ]);
                        break;
                    case "organization":
                        await Promise.all([
                            prefetchOrganizations(),
                            prefetchDepartments(),
                        ]);
                        break;
                    case "location":
                        await Promise.all([
                            prefetchLocations(),
                            prefetchLocationLookups(),
                            prefetchOrganizations(),
                        ]);
                        break;
                }
            } catch (error) {
                console.warn(`${page} admin data prefetch failed:`, error);
            }
        },
        [
            prefetchDepartments,
            prefetchOrganizations,
            prefetchLocations,
            prefetchLocationLookups,
        ]
    );

    return {
        prefetchDepartments,
        prefetchOrganizations,
        prefetchLocations,
        prefetchLocationLookups,
        prefetchAllAdminData,
        prefetchAdminPage,
    };
};
